import { convertToCartographicArray, type Line } from '@aus-platform/cesium';
import { Math as CesiumMath, HeadingPitchRange } from 'cesium';
import { isEmpty, isNil, isNumber, maxBy } from 'lodash';
import type { ILineTool } from 'src/map-3d/shared';
import { type FeatureResponseData, FeatureType } from 'src/shared/api';
import type { CesiumProxy } from 'src/shared/cesium';
import { MarkArea, type WorkspaceLayer } from '../../shared';
import { getBoundaryConfig, getSelectedLineStyle } from './constants';
import type {
  CrossSectionFormState,
  CrossSectionLineData,
  Point2D,
} from './types';

/**
 * Deletes blue overlay lines that are used to highlight selected lines.
 */
export const deleteBlueOverlayLines = (
  lineTool: ILineTool | undefined,
): void => {
  if (isNil(lineTool)) {
    return;
  }

  const blueLines = lineTool
    .getLinesByProperty('crossSectionSelectedLine', true)
    .map((line) => line.id);

  if (isEmpty(blueLines)) {
    return;
  }

  blueLines.forEach((id) => lineTool.deleteById(id));
};

/**
 * Deletes lines by marked area type.
 */
export const deleteLinesByMarkedArea = (
  lineTool: ILineTool | undefined,
  markArea: MarkArea,
): void => {
  if (isNil(lineTool)) {
    return;
  }

  const lines = lineTool
    .getLinesByProperty('crossSectionLines', markArea)
    .map((line) => line.id);

  if (isNil(lines)) {
    return;
  }

  for (const lineId of lines) {
    lineTool.deleteById(lineId);
  }
};

/**
 * Loads line features from workspace into the line tool.
 */
export const loadLinesFromWorkspace = (
  lineTool: ILineTool | undefined,
  featureResponse: FeatureResponseData[],
): void => {
  if (isNil(lineTool) || isEmpty(featureResponse)) {
    return;
  }

  featureResponse.forEach((feature) => {
    if (feature.type === FeatureType.LineString) {
      lineTool.importWKT(
        feature.geometry,
        feature.id,
        { crossSectionLines: MarkArea.LayerFromWorkspace },
        feature.name,
      );
    }
  });
};

/**
 * Handles feature focus event to select a line from workspace.
 */
export const handleFeatureFocus = (
  featureId: string,
  lineTool: ILineTool | undefined,
  currentMarkedArea: MarkArea,
  sampleDistance: string,
  setValues: React.Dispatch<React.SetStateAction<CrossSectionFormState>>,
): void => {
  if (
    currentMarkedArea !== MarkArea.LayerFromWorkspace ||
    isNil(lineTool) ||
    isEmpty(featureId)
  ) {
    return;
  }

  const clickedLine = lineTool.getLine(featureId);

  if (isNil(clickedLine)) {
    return;
  }

  deleteBlueOverlayLines(lineTool);

  const boundaryWidth = Number(sampleDistance);

  if (isNaN(boundaryWidth)) {
    return;
  }

  const importedLines = lineTool.importWKT(
    clickedLine.exportWKT().wkt,
    `${featureId}_selected`,
    { crossSectionSelectedLine: true },
    undefined,
    getSelectedLineStyle(boundaryWidth),
  );

  if (!isNil(importedLines) && !isEmpty(importedLines)) {
    setValues((prevValues) => ({
      ...prevValues,
      selectedLine: importedLines[0],
    }));
  }
};

/**
 * Auto-selects the highest z-index point cloud layer.
 */
export const autoSelectPointCloudLayer = (
  visiblePointCloudLayers: WorkspaceLayer[],
  setValues: React.Dispatch<React.SetStateAction<CrossSectionFormState>>,
): WorkspaceLayer | null => {
  if (isNil(visiblePointCloudLayers) || isEmpty(visiblePointCloudLayers)) {
    return null;
  }

  const highestZIndexLayer =
    visiblePointCloudLayers.length === 1
      ? visiblePointCloudLayers[0]
      : maxBy(visiblePointCloudLayers, (layer) => layer.zIndex ?? 0);

  if (isNil(highestZIndexLayer)) {
    return null;
  }

  setValues((prev) => ({
    ...prev,
    pointCloudLayer: {
      label: highestZIndexLayer.name,
      value: highestZIndexLayer,
    },
  }));

  return highestZIndexLayer;
};

/**
 * Updates line styles when sample distance changes.
 */
export const updateLineStylesForSampleDistance = (
  lineTool: ILineTool | undefined,
  boundaryWidth: number,
): void => {
  if (isNil(lineTool) || isNaN(boundaryWidth)) {
    return;
  }

  const boundaryConfig = getBoundaryConfig(boundaryWidth);

  // Update drawn lines.
  const drawnLines = lineTool.getLinesByProperty(
    'crossSectionLines',
    MarkArea.Draw,
  );

  if (!isEmpty(drawnLines)) {
    drawnLines.forEach((line) => {
      lineTool.updateLineStyle(line.id, boundaryConfig);
    });
  }

  // Update selected blue overlay lines.
  const selectedLines = lineTool.getLinesByProperty(
    'crossSectionSelectedLine',
    true,
  );

  if (!isEmpty(selectedLines)) {
    selectedLines.forEach((line) => {
      lineTool.updateLineStyle(line.id, boundaryConfig);
    });
  }
};

/**
 * Prepares cross section line data for submission.
 */
export const prepareCrossSectionLineData = (
  selectedLine: Line,
  sampleDistance: string,
): CrossSectionLineData | null => {
  const buffer = Number(sampleDistance);

  if (!isNumber(buffer) || isNaN(buffer) || buffer < 0) {
    return null;
  }

  const cartographicPositions = convertToCartographicArray(
    selectedLine.positions,
  );

  if (isEmpty(cartographicPositions)) {
    return null;
  }

  const pathWGS84: Point2D[] = cartographicPositions.map((cartographic) => [
    cartographic.longitude,
    cartographic.latitude,
  ]);

  return {
    pathWGS84,
    buffer,
    line: selectedLine,
  };
};

/**
 * Flies camera to the selected line's bounding sphere.
 */
export const flyToSelectedLine = (
  selectedLine: Line,
  cesiumProxy: CesiumProxy | null,
): void => {
  if (isNil(selectedLine?.boundingSphere) || isNil(cesiumProxy?.viewer)) {
    return;
  }

  const offset = new HeadingPitchRange(0, -CesiumMath.PI_OVER_TWO, 0);

  cesiumProxy.viewer.camera.flyToBoundingSphere(selectedLine.boundingSphere, {
    offset,
  });
};
